import {useEffect, useState} from 'react';

/**
 * Custom hook that debounces a value by a specified delay.
 * Useful for delaying API calls on rapid user input (e.g., search fields).
 * 
 * The hook prevents the debounced value from updating until the user has stopped
 * changing the input value for the specified delay period. If the value changes
 * within the delay period, the timeout is cleared and restarted.
 *
 * @param {object} params - The hook parameters
 * @param {*} params.value - The value to debounce (can be any type)
 * @param {number} params.delay - The debounce delay in milliseconds
 * @returns {*} The debounced value (same type as input value)
 * 
 * @example
 * const debouncedSearch = useDebounce({value: searchQuery, delay: 400});
 */
export default function useDebounce({value, delay}) {
    // State and setters for debounced value
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        // Set debouncedValue to value (passed in) after the specified delay
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        // Return a cleanup function that will be called every time useEffect is re-called.
        // useEffect will only be re-called if value or delay changes (see the dependencies array below).
        // This is how we prevent debouncedValue from changing if value is changed within the delay period.
        // Timeout gets cleared and restarted.
        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
}
